import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles, Clock, ArrowRight, Check, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { useCart } from '../../context/CartContext';
import { toast } from 'sonner';
import { getOptimizedImageUrl } from '../../utils/image';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ProductOfDay = () => {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false); // Start with false
  const { addToCart } = useCart();

  useEffect(() => {
    fetchFeaturedProduct();
  }, []);

  const fetchFeaturedProduct = async () => {
    try {
      const response = await fetch(`${API_URL}/api/products?limit=1&lite=true`);
      const data = await response.json();
      if (data.length > 0) {
        setProduct(data[0]);
      }
    } catch (error) {
      console.error('Error fetching product:', error);
    }
    setLoading(false);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(price);
  };

  const handleAddToCart = () => {
    if (product) {
      addToCart(product);
      toast.success(`${product.name} added to cart!`);
    }
  };

  // Don't show loading spinner, just don't render until data arrives
  if (loading || !product) {
    return null;
  }

  const discount = product.originalPrice > product.price 
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : 0;

  // Extract benefits from tags or use defaults
  const benefits = product.tags?.slice(0, 4) || ['Premium Quality', 'Authentic', 'Energized', 'Certified'];

  return (
    <section className="py-12 lg:py-16" data-testid="product-of-day-section">
      <div className="container mx-auto px-4 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 border border-amber-100 shadow-lg"
        >
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-gold/5 rounded-full blur-3xl" />
          
          <div className="relative grid lg:grid-cols-2 gap-6 lg:gap-12 p-6 lg:p-10">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-square lg:aspect-[4/3] rounded-xl overflow-hidden shadow-lg">
                <img
                  src={getOptimizedImageUrl(product.image, '600x600')}
                  alt={product.name}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 rounded-full gold-gradient text-white text-sm font-bold shadow-lg">
                <Sparkles className="w-4 h-4" />
                Featured Product
              </div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="flex flex-col justify-center"
            >
              {/* Timer Banner */}
              <div className="flex items-center gap-2 text-sm text-gold mb-4">
                <Clock className="w-4 h-4" />
                <span className="font-medium">Special Offer - Limited Time!</span>
              </div>

              <h3 className="font-serif text-xl lg:text-2xl font-bold text-foreground mb-3">
                {product.name}
              </h3>

              <p className="text-muted-foreground text-sm lg:text-base mb-4 leading-relaxed line-clamp-3">
                {product.description}
              </p>

              {/* Benefits */}
              <div className="grid grid-cols-2 gap-2 mb-6">
                {benefits.map((benefit) => (
                  <div
                    key={benefit}
                    className="flex items-center gap-2 text-sm text-foreground"
                  >
                    <Check className="w-4 h-4 text-gold flex-shrink-0" />
                    <span>{benefit}</span>
                  </div>
                ))}
              </div>

              {/* Price */}
              <div className="flex items-center gap-3 mb-6">
                <span className="text-gold font-bold text-3xl">
                  {formatPrice(product.price)}
                </span>
                {discount > 0 && (
                  <>
                    <span className="text-muted-foreground line-through text-lg">
                      {formatPrice(product.originalPrice)}
                    </span>
                    <span className="px-2 py-1 rounded-md bg-green-100 text-green-700 text-sm font-bold">
                      {discount}% OFF
                    </span>
                  </>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  onClick={handleAddToCart}
                  size="lg"
                  className="gold-gradient text-white hover:opacity-90 rounded-full px-8 font-semibold shadow-lg shadow-gold/20"
                  data-testid="pod-add-to-cart"
                >
                  Add to Cart
                </Button>
                <Button
                  asChild
                  variant="outline"
                  size="lg"
                  className="border-gold text-gold hover:bg-gold/10 rounded-full px-6 group"
                  data-testid="pod-view-details"
                >
                  <Link to={`/product/${product.id}`}>
                    View Details
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
